import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { THREAD_ID_STORAGE_KEY, readScopedItem, scopedStorageKey } from '../constants/storage';
import type { Message, ChatWidgetProps, SessionConfig } from '../types';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { getNextMessageId } from '../utils/chatHelpers';
import { messageAnalyticsContext } from '../utils/messageAnalyticsContext';
import {
  clearThreadToken,
  getThreadToken,
  storeThreadToken,
  threadCapabilityHeaders,
} from '../utils/threadCapability';

export interface ThreadIssued {
  threadId?: string | null;
  threadToken?: string | null;
}

/** Server timestamps become ISO 8601 so the UI can render them in the widget locale. */
function toIsoTimestamp(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.toISOString();
    if (typeof value === 'string' && value.trim()) return value;
  }
  return new Date().toISOString();
}

interface UseThreadOptions {
  threadIdProp: string | null | undefined;
  threadIdControlled?: boolean;
  threadEndpoint: string | null | undefined;
  messageEndpoint: string | null | undefined;
  apiBaseUrl: string | null | undefined;
  apiKey: string | null | undefined;
  widgetAuthToken?: string | null;
  orgId?: string | null;
  agentId?: string | null;
  provider?: string;
  channel: string;
  threadCreateEndpoint: string | null | undefined;
  threadUpdateEndpoint: string | null | undefined;
  threadDeleteEndpoint: string | null | undefined;
  threadPollIntervalMs: number;
  persistThread: boolean;
  lazyLoad?: boolean;
  minimized: boolean;
  subject: string;
  department: string;
  customFields: Record<string, unknown>;
  metaExtras?: Record<string, unknown>;
  sessionConfig?: SessionConfig;
  buildThreadUrl?: ChatWidgetProps['buildThreadUrl'];
  buildThreadRequest?: ChatWidgetProps['buildThreadRequest'];
  buildThreadUpdateUrl?: ChatWidgetProps['buildThreadUpdateUrl'];
  buildThreadDeleteUrl?: ChatWidgetProps['buildThreadDeleteUrl'];
  parseThreadResponse?: ChatWidgetProps['parseThreadResponse'];
  parseThreadCreateResponse?: ChatWidgetProps['parseThreadCreateResponse'];
  onThreadCreate?: ChatWidgetProps['onThreadCreate'];
  onThreadUpdate?: ChatWidgetProps['onThreadUpdate'];
  onThreadDelete?: ChatWidgetProps['onThreadDelete'];
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

function computeUrlWithId(base: string | null | undefined, id: string): string | null {
  if (!base) return null;
  const eid = encodeURIComponent(id);
  if (base.includes('{id}')) return base.replace('{id}', eid);
  if (base.includes(':id')) return base.replace(':id', eid);
  return `${base.replace(/\/$/, '')}/${eid}`;
}

function getThreadStorage(sessionConfig?: SessionConfig): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    if (sessionConfig?.storage === 'session') return window.sessionStorage;
    return window.localStorage;
  } catch {
    return null;
  }
}

const messageKey = (message: Message) => `${message.sender}\u0000${message.text.trim()}`;

/** Same message if both timestamps parse and lie within 10 minutes (or either cannot be parsed). */
function closeInTime(a: string, b: string): boolean {
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  return Number.isNaN(ta) || Number.isNaN(tb) || Math.abs(ta - tb) <= 10 * 60_000;
}

/**
 * Reply fields the thread endpoint may not persist. When the server copy has
 * no value (undefined / null / '' / []) the local one survives the refresh,
 * so e.g. suggestion chips do not vanish (and steal keyboard focus) ~100 ms
 * after a reply; a non-empty server value wins. `type` rides along so kept
 * `richContent` still renders as the same kind of card.
 */
const CLIENT_ONLY_REPLY_FIELDS = [
  'suggestions',
  'sources',
  'toolCalls',
  'thinking',
  'richContent',
  'type',
] as const;

const isEmptyFieldValue = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

function clientOnlyFieldsToKeep(local: Message, serverMessage: Message): Partial<Message> | null {
  const kept: Partial<Message> = {};
  let any = false;
  for (const field of CLIENT_ONLY_REPLY_FIELDS) {
    if (isEmptyFieldValue(serverMessage[field]) && !isEmptyFieldValue(local[field])) {
      Object.assign(kept, { [field]: local[field] });
      any = true;
    }
  }
  return any ? kept : null;
}

/**
 * Server snapshot -> next message list. A server message already on screen
 * (same id, else same sender + text at a nearby position and time) keeps its
 * local id so the bubble does not remount; `serverIds` records local -> server
 * id for those and the kept message carries it as `serverId`. A local message
 * whose `serverId` (from the chat reply) equals the server id matches first.
 * Local-only messages (cart confirmations etc.) the server has not persisted
 * are re-inserted after the message they followed.
 */
function mergeServerMessagesWithLocalMessages(
  serverMessages: Message[],
  currentMessages: Message[],
  serverIds: Map<string, string>
): Message[] {
  const persisted = currentMessages.filter(message => !message.localOnly);
  const matched = new Set<number>();
  let cursor = 0;
  const adopted = serverMessages.map(serverMessage => {
    let index = persisted.findIndex(
      (m, i) => !matched.has(i) && (m.id === serverMessage.id || m.serverId === serverMessage.id)
    );
    if (index < 0) {
      const key = messageKey(serverMessage);
      index = persisted.findIndex(
        (m, i) =>
          i >= cursor &&
          !matched.has(i) &&
          messageKey(m) === key &&
          closeInTime(m.timestamp, serverMessage.timestamp)
      );
    }
    if (index < 0) return serverMessage;
    matched.add(index);
    cursor = Math.max(cursor, index + 1);
    const local = persisted[index];
    if (local.id !== serverMessage.id) serverIds.set(local.id, serverMessage.id);
    // Optimistic feedback the server has not persisted yet survives the refresh;
    // a reaction carried by the server wins.
    const keepFeedback =
      serverMessage.reaction === undefined &&
      (local.reaction !== undefined || local.feedbackComment !== undefined);
    const keptFields = clientOnlyFieldsToKeep(local, serverMessage);
    if (local.id === serverMessage.id && !keepFeedback && !keptFields) return serverMessage;
    return {
      ...serverMessage,
      ...keptFields,
      id: local.id,
      ...(local.id !== serverMessage.id ? { serverId: serverMessage.id } : {}),
      ...(keepFeedback ? { reaction: local.reaction, feedbackComment: local.feedbackComment } : {}),
    };
  });

  const persistedKeys = new Set(serverMessages.map(messageKey));
  const localGroups = new Map<string, Message[]>();
  let anchorId = '';
  for (const message of currentMessages) {
    if (!message.localOnly) {
      anchorId = message.id;
      continue;
    }
    if (persistedKeys.has(messageKey(message))) continue;
    const group = localGroups.get(anchorId) || [];
    group.push(message);
    localGroups.set(anchorId, group);
  }
  if (localGroups.size === 0) return adopted;

  const merged: Message[] = [...(localGroups.get('') || [])];
  const insertedAnchors = new Set<string>(['']);
  for (const message of adopted) {
    merged.push(message);
    const group = localGroups.get(message.id);
    if (group) {
      merged.push(...group);
      insertedAnchors.add(message.id);
    }
  }
  for (const [groupAnchorId, group] of localGroups) {
    if (!insertedAnchors.has(groupAnchorId)) merged.push(...group);
  }
  return merged;
}

export function useThread(options: UseThreadOptions) {
  const {
    threadIdProp,
    threadIdControlled = false,
    threadEndpoint,
    messageEndpoint,
    apiBaseUrl,
    apiKey,
    widgetAuthToken,
    orgId,
    agentId,
    provider,
    channel,
    threadCreateEndpoint,
    threadUpdateEndpoint,
    threadDeleteEndpoint,
    threadPollIntervalMs,
    persistThread,
    lazyLoad,
    minimized,
    subject,
    department,
    customFields,
    metaExtras,
    sessionConfig,
    buildThreadUrl,
    buildThreadRequest,
    buildThreadUpdateUrl,
    buildThreadDeleteUrl,
    parseThreadResponse,
    parseThreadCreateResponse,
    onThreadCreate,
    onThreadUpdate,
    onThreadDelete,
    trackAnalytics,
    setMessages,
  } = options;

  // Thread id storage is namespaced per org/agent so two widgets on one origin
  // do not share a thread; the legacy global key is used until both are known.
  const threadIdStorageKey = scopedStorageKey(THREAD_ID_STORAGE_KEY, { orgId, agentId });
  const [threadId, setThreadId] = useState<string | null>(() => {
    if (threadIdProp) return threadIdProp;
    if (!persistThread) return null;
    const storage = getThreadStorage(sessionConfig);
    if (!storage) return null;
    try {
      return readScopedItem(storage, THREAD_ID_STORAGE_KEY, { orgId, agentId });
    } catch {
      return null; // storage unavailable
    }
  });

  const isMountedRef = useRef(true);
  const threadIdRef = useRef<string | null>(threadId);
  const fetchInFlightRef = useRef(false);
  const pendingFetchRef = useRef(false);
  const activeFetchControllerRef = useRef<AbortController | null>(null);
  // local message id -> server id for messages that kept their id across a refresh.
  const serverIdsRef = useRef(new Map<string, string>());

  useEffect(() => {
    threadIdRef.current = threadId;
    serverIdsRef.current.clear();
  }, [threadId]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      pendingFetchRef.current = false;
      activeFetchControllerRef.current?.abort();
      activeFetchControllerRef.current = null;
    };
  }, []);

  // Keep internal threadId in sync with prop
  useEffect(() => {
    if (!threadIdControlled) return;
    if (threadIdProp !== threadId) setThreadId(threadIdProp || null);
  }, [threadIdProp, threadIdControlled, threadId]);

  // Persist thread id locally
  useEffect(() => {
    if (!persistThread) return;
    const storage = getThreadStorage(sessionConfig);
    if (!storage) return;
    try {
      if (threadId) storage.setItem(threadIdStorageKey, threadId);
      else storage.removeItem(threadIdStorageKey);
    } catch {
      // Storage unavailable
    }
  }, [threadId, persistThread, sessionConfig, threadIdStorageKey]);

  // Infer a sensible thread base endpoint
  const threadBase = useMemo(() => {
    if (threadEndpoint) return threadEndpoint;
    if (messageEndpoint) {
      try {
        const trimmedMessageEndpoint = messageEndpoint.trim();
        const windowOrigin =
          typeof window !== 'undefined' && window.location?.origin ? window.location.origin : null;
        const u = windowOrigin
          ? new URL(trimmedMessageEndpoint, windowOrigin)
          : new URL(trimmedMessageEndpoint);

        if (!['http:', 'https:'].includes(u.protocol)) return null;

        let basePath = u.pathname.replace(/\/$/, '');
        if (basePath.endsWith('/api/response/stateset')) {
          basePath = basePath.replace(/\/stateset$/, '/threads');
        } else if (basePath.endsWith('/api/chat')) {
          basePath = basePath.replace(/\/chat$/, '/threads');
        } else if (basePath.endsWith('/api/response')) {
          basePath = `${basePath}/threads`;
        } else {
          basePath = '/api/threads';
        }
        return `${u.origin}${basePath}`;
      } catch {
        return null; // messageEndpoint is not a valid URL
      }
    }
    if (apiBaseUrl) return `${apiBaseUrl.replace(/\/$/, '')}/threads`;
    return null;
  }, [threadEndpoint, messageEndpoint, apiBaseUrl]);

  const withAuthHeaders = useCallback(
    (headers: Record<string, string> = {}): Record<string, string> => ({
      'Content-Type': 'application/json',
      ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}),
      ...(widgetAuthToken ? { 'x-widget-token': widgetAuthToken } : {}),
      ...(orgId ? { 'x-org-id': orgId } : {}),
      ...(agentId ? { 'x-agent-id': agentId } : {}),
      ...headers,
    }),
    [apiKey, widgetAuthToken, orgId, agentId]
  );

  const fetchThreadRef = useRef<() => Promise<void>>(async () => {});

  // Callback/config options fetchThread reads, refreshed every render. Hosts (and
  // ChatWidget) pass these inline, so depending on them would give fetchThread a
  // new identity each render and the open-widget effect below would refetch on
  // every render (merge -> render -> fetch -> merge ...). fetchThread only
  // depends on what genuinely selects the request: thread id, endpoint,
  // channel, auth and the token storage kind.
  const fetchOptionsRef = useRef({
    buildThreadUrl,
    buildThreadRequest,
    parseThreadResponse,
    trackAnalytics,
    setMessages,
    sessionConfig,
  });
  fetchOptionsRef.current = {
    buildThreadUrl,
    buildThreadRequest,
    parseThreadResponse,
    trackAnalytics,
    setMessages,
    sessionConfig,
  };
  const sessionStorageKind = sessionConfig?.storage;

  // Fetch thread messages
  const fetchThread = useCallback(async () => {
    const {
      buildThreadUrl,
      buildThreadRequest,
      parseThreadResponse,
      trackAnalytics,
      setMessages,
      sessionConfig,
    } = fetchOptionsRef.current;
    if (!threadBase || !threadId) return;
    if (fetchInFlightRef.current) {
      pendingFetchRef.current = true;
      return;
    }
    const requestedThreadId = threadId;
    const controller = new AbortController();
    fetchInFlightRef.current = true;
    activeFetchControllerRef.current = controller;
    try {
      const url =
        typeof buildThreadUrl === 'function'
          ? buildThreadUrl({ threadId: requestedThreadId, endpoint: threadBase, channel })
          : (() => {
              const base = threadBase.replace(/\/$/, '');
              const q = channel ? `?channel=${encodeURIComponent(channel)}` : '';
              return `${base}/${encodeURIComponent(requestedThreadId)}${q}`;
            })();
      const defaultReq: RequestInit = {
        method: 'GET',
        credentials: 'include',
        // Echo the per-thread capability token so thread reads keep working
        // once the server enforces WIDGET_THREAD_CAP_ENFORCE.
        headers: withAuthHeaders(threadCapabilityHeaders(requestedThreadId, sessionConfig)),
      };
      const reqInit =
        typeof buildThreadRequest === 'function'
          ? buildThreadRequest({ threadId: requestedThreadId, endpoint: threadBase })
          : defaultReq;
      const res = await fetchWithTimeout(url, {
        ...reqInit,
        credentials: reqInit.credentials ?? 'include',
        signal: controller.signal,
        timeoutMs: 15_000,
      });
      if (!res.ok) throw new Error(`Thread fetch failed: ${res.status}`);
      const data = await res.json();
      if (controller.signal.aborted || !isMountedRef.current) return;
      const parsed = typeof parseThreadResponse === 'function' ? parseThreadResponse(data) : null;
      let serverMessages = parsed?.messages;
      if (!serverMessages) {
        const raw = data?.messages || data?.thread?.messages || [];
        if (Array.isArray(raw)) {
          serverMessages = raw.map((m: Record<string, unknown>, i: number) => ({
            id: (m.id ?? m.messageId ?? `${requestedThreadId}-${i}`) as string,
            sender:
              (typeof m.sender === 'string' && m.sender.toLowerCase() === 'user') ||
              (typeof m.role === 'string' && m.role.toLowerCase() === 'user')
                ? ('user' as const)
                : ('ai' as const),
            text: (m.text ?? m.message ?? m.content ?? '') as string,
            timestamp: toIsoTimestamp(m.timestamp),
            status: 'delivered' as const,
            type: (m.type ?? m.messageType) as Message['type'],
            threadSource: String(
              typeof m.sender === 'string' ? m.sender : typeof m.role === 'string' ? m.role : ''
            ).trim(),
            threadRole: String(typeof m.role === 'string' ? m.role : '').trim(),
            richContent: (m.richContent ?? m.rich_content) as Message['richContent'],
            suggestions: Array.isArray(m.suggestions) ? (m.suggestions as string[]) : undefined,
            sources: Array.isArray(m.sources) ? (m.sources as Message['sources']) : undefined,
            toolCalls: Array.isArray(m.toolCalls)
              ? (m.toolCalls as Message['toolCalls'])
              : undefined,
            thinking: m.thinking as string | undefined,
            reaction: m.reaction === 'up' || m.reaction === 'down' ? m.reaction : undefined,
          }));
        }
      }
      // Ignore stale responses that return after thread switch.
      if (threadIdRef.current !== requestedThreadId) return;
      if (serverMessages && serverMessages.length >= 0) {
        trackAnalytics('thread_fetch', { ok: true, count: serverMessages.length });
        if (serverMessages.length > 0) {
          setMessages(currentMessages =>
            mergeServerMessagesWithLocalMessages(
              serverMessages,
              currentMessages,
              serverIdsRef.current
            )
          );
        }
      }
    } catch (err) {
      const isAbortError =
        controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError');
      if (isAbortError) return;
      trackAnalytics('thread_fetch', {
        ok: false,
        error: err instanceof Error ? err.message : 'unknown',
      });
    } finally {
      if (activeFetchControllerRef.current === controller) {
        activeFetchControllerRef.current = null;
      }
      fetchInFlightRef.current = false;
      if (pendingFetchRef.current && isMountedRef.current) {
        pendingFetchRef.current = false;
        void Promise.resolve().then(() => {
          if (isMountedRef.current) {
            void fetchThreadRef.current();
          }
        });
      }
    }
    // sessionStorageKind: the token lookup reads sessionConfig from the ref, but a
    // storage switch must still produce a fresh fetchThread.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadBase, threadId, channel, withAuthHeaders, sessionStorageKind]);

  // Stable ref so polling doesn't restart when fetchThread identity changes
  useEffect(() => {
    fetchThreadRef.current = fetchThread;
  }, [fetchThread]);

  // Abort stale request when closing widget to avoid background updates.
  useEffect(() => {
    if (minimized) {
      pendingFetchRef.current = false;
      activeFetchControllerRef.current?.abort();
    }
  }, [minimized]);

  // Fetch on open or when thread changes
  useEffect(() => {
    if (!minimized) fetchThread();
  }, [minimized, fetchThread]);

  // While closed (and not lazy) keep the thread warm on mount / thread change.
  // Open-widget fetches belong to the effect above; running here too would
  // double-fetch on every open, and a fetch on close would undo the abort above.
  const minimizedRef = useRef(minimized);
  minimizedRef.current = minimized;
  useEffect(() => {
    if (lazyLoad || !minimizedRef.current) return;
    fetchThread();
  }, [threadId, threadBase, lazyLoad]); // eslint-disable-line react-hooks/exhaustive-deps

  // Optional polling (use ref-stable fetchThread to prevent interval restarts)
  useEffect(() => {
    if (!threadPollIntervalMs || threadPollIntervalMs <= 0) return;
    if (minimized) return;
    if (!threadId) return;
    const id = setInterval(() => {
      fetchThreadRef.current();
    }, threadPollIntervalMs);
    return () => clearInterval(id);
  }, [threadPollIntervalMs, minimized, threadId]);

  const createThread = useCallback(
    async ({
      subject: s,
      message: m,
      meta,
      ch,
    }: {
      subject?: string;
      message?: string;
      meta?: Record<string, unknown>;
      ch?: string;
    } = {}) => {
      const endpoint = threadCreateEndpoint;
      if (!endpoint) throw new Error('threadCreateEndpoint is not configured');
      const analyticsContext = messageAnalyticsContext(sessionConfig);
      const payload = {
        subject: s || subject || 'New conversation',
        message: m || '',
        ...(provider ? { provider } : {}),
        meta: {
          ...analyticsContext,
          department,
          customFields,
          ...(metaExtras || {}),
          ...(meta || {}),
        },
        sessionId: analyticsContext.sessionId,
        source: 'response-chat-widget',
        channel: ch || channel,
      };
      const res = await fetchWithTimeout(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: withAuthHeaders(),
        body: JSON.stringify(payload),
        timeoutMs: 15_000,
      });
      if (!res.ok) {
        let serverError = '';
        try {
          const payload = (await res.json()) as Record<string, unknown>;
          if (typeof payload?.error === 'string') serverError = payload.error;
          else if (typeof payload?.message === 'string') serverError = payload.message;
        } catch {
          // Keep generic status code message if body is not JSON.
        }
        throw new Error(
          `Thread create failed: ${res.status}${serverError ? ` (${serverError})` : ''}`
        );
      }
      const data = await res.json();
      const parsed =
        typeof parseThreadCreateResponse === 'function' ? parseThreadCreateResponse(data) : null;
      const newId = parsed?.threadId ?? data?.threadId ?? data?.id ?? data?.thread?.id;
      if (newId) {
        setThreadId(newId);
        // Persist the per-thread capability token; it must be echoed on all
        // later reads/mutations of this thread (x-widget-thread-token).
        storeThreadToken(newId, data?.threadToken ?? data?.thread_token, sessionConfig);
      }
      onThreadCreate?.(newId, data);
      trackAnalytics('thread_create', { ok: !!newId });
      if (m) {
        const now = new Date();
        setMessages(prev => [
          ...prev,
          {
            id: getNextMessageId(),
            sender: 'user' as const,
            text: m,
            timestamp: now.toISOString(),
            status: 'sent' as const,
          },
        ]);
      }
      if (newId) {
        try {
          await fetchThread();
        } catch {
          // Thread refresh after create is best-effort; failure is non-critical
        }
      }
      return { threadId: newId, raw: data };
    },
    [
      threadCreateEndpoint,
      subject,
      department,
      customFields,
      metaExtras,
      provider,
      channel,
      onThreadCreate,
      withAuthHeaders,
      trackAnalytics,
      parseThreadCreateResponse,
      fetchThread,
      setMessages,
      sessionConfig,
    ]
  );

  const updateThread = useCallback(
    async (id: string, patch: Record<string, unknown> = {}) => {
      const url = buildThreadUpdateUrl
        ? buildThreadUpdateUrl({ id, endpoint: threadUpdateEndpoint || threadBase || '' })
        : computeUrlWithId(threadUpdateEndpoint || threadBase, id);
      if (!url) throw new Error('No thread update URL configured');
      const res = await fetchWithTimeout(url, {
        method: 'PATCH',
        credentials: 'include',
        headers: withAuthHeaders(threadCapabilityHeaders(id, sessionConfig)),
        body: JSON.stringify(patch),
        timeoutMs: 15_000,
      });
      if (!res.ok) throw new Error(`Thread update failed: ${res.status}`);
      const data = await res.json();
      onThreadUpdate?.(id, data);
      trackAnalytics('thread_update', { ok: true });
      return data;
    },
    [
      threadUpdateEndpoint,
      threadBase,
      buildThreadUpdateUrl,
      withAuthHeaders,
      onThreadUpdate,
      trackAnalytics,
      sessionConfig,
    ]
  );

  const deleteThread = useCallback(
    async (id: string) => {
      const url = buildThreadDeleteUrl
        ? buildThreadDeleteUrl({ id, endpoint: threadDeleteEndpoint || threadBase || '' })
        : computeUrlWithId(threadDeleteEndpoint || threadBase, id);
      if (!url) throw new Error('No thread delete URL configured');
      const res = await fetchWithTimeout(url, {
        method: 'DELETE',
        credentials: 'include',
        headers: withAuthHeaders(threadCapabilityHeaders(id, sessionConfig)),
        timeoutMs: 15_000,
      });
      const ok = res.ok;
      if (ok) clearThreadToken(id, sessionConfig);
      try {
        await res.json();
      } catch {
        // DELETE responses may have empty body; safe to ignore JSON parse failure
      }
      if (threadId === id) {
        setThreadId(null);
        setMessages([]);
      }
      onThreadDelete?.(id);
      trackAnalytics('thread_delete', { ok });
      return { ok };
    },
    [
      threadDeleteEndpoint,
      threadBase,
      buildThreadDeleteUrl,
      withAuthHeaders,
      threadId,
      onThreadDelete,
      trackAnalytics,
      setMessages,
      sessionConfig,
    ]
  );

  /**
   * Chat responses (JSON `threadToken` / SSE `thread` event) hand back the
   * capability for a thread the server created implicitly. Persist it exactly
   * like the `POST /api/threads` path does, adopt the id when the widget has no
   * thread yet, and never overwrite a token we already hold for that thread.
   */
  const onThreadIssued = useCallback(
    ({ threadId: issuedId, threadToken }: ThreadIssued) => {
      if (!issuedId) return;
      if (!threadIdRef.current && !threadIdControlled) {
        threadIdRef.current = issuedId;
        setThreadId(issuedId);
      }
      if (!threadToken) return;
      if (getThreadToken(issuedId, sessionConfig)) return;
      storeThreadToken(issuedId, threadToken, sessionConfig);
    },
    [sessionConfig, threadIdControlled]
  );

  /** Server id of a message that kept its local id across a thread refresh (else the id itself). */
  const resolveServerMessageId = useCallback(
    (id: string) => serverIdsRef.current.get(id) ?? id,
    []
  );

  // Memoized so consumers that depend on `[thread]` don't rebuild every render.
  return useMemo(
    () => ({
      threadId,
      setThreadId,
      threadBase,
      fetchThread,
      createThread,
      updateThread,
      deleteThread,
      withAuthHeaders,
      onThreadIssued,
      resolveServerMessageId,
    }),
    [
      threadId,
      setThreadId,
      threadBase,
      fetchThread,
      createThread,
      updateThread,
      deleteThread,
      withAuthHeaders,
      onThreadIssued,
      resolveServerMessageId,
    ]
  );
}
